/**
 * Special adjust flow（特殊调整路径）。
 *
 * 统一封装 special 路径节点的 entry 判定、detail flip、后端回显还原、toPaper 配对与 Dr/Cr 渲染分列。
 *
 * ## 入口（全局 / scoped）
 * - `SPECIAL_ADJUST_GLOBAL_CODES`：全局固定走 special（无需注册）；默认 `BS-CL-DTD` / `BS-EQ-DIV`
 * - `SPECIAL_ADJUST_SCOPED_ELIGIBLE_CODES`：允许 WP register 的 code；默认 `BS-CA-TAXR` / `BS-CL-TAXP`（Tax b/f）
 * - 运行时 Set（store `specialDtdPathCodes`）：已注册的 scoped code
 * - 判定：`usesSpecialAdjustPathScoped(code, scopedCodes)` =
 *   全局 **或**（eligible **且** 已注册）
 * - 节点专用逻辑仍用 `isDtdClFsCode` / `isTaxRefundableBfDtdCode` / `isTaxPayableBfDtdCode` / `isDividendEqCode`，不随集合自动扩散
 *
 * 带符号 adjustment 约定：负数 = 落在 originalEntry 侧；正数 = 落在对侧。
 * originalEntry 兼容规则（默认 Cr，与历史 DTD 行为一致）：
 * - originalEntry = Cr：+ → Dr（对侧）；- → Cr（本侧）
 * - originalEntry = Dr：+ → Cr（对侧）；- → Dr（本侧）
 * - 保存时 adjustmentAmount 始终为绝对值（正数）；detail 行内 adjustment 为 flip 后存储
 *
 * originalEntry 一律从 FsCategory 节点读取；
 * BS-CA-TAXR / BS-EQ-DIV 为 Dr 侧，节点缺失时 fallback 为 Dr。
 * BS-CL-TAXP（Tax payable b/f）FS 虽为负债 Cr，分列/flip 与 TAXR 对称，强制按 Dr-side special。
 */

import {useFsCategoryStore} from '@/store/audit/fsCategoryStore';

// ── Types & constants ───────────────────────────────────────────────────

export type DtdDrCrEntry = 'Dr' | 'Cr';

export const DTD_CL_CODE = 'BS-CL-DTD';
export const DIR_CA_CODE = 'BS-CA-DIR';
/** DividendWp：全局 special；originalEntry 取自 FS，失败默认 Dr */
export const DIVIDEND_EQ_CODE = 'BS-EQ-DIV';
/** TaxWp「Tax (refundable) b/f」：仅 TaxWp 发起时注册 scoped special 路径；originalEntry 取自 FS 节点（Dr） */
export const TAX_REFUNDABLE_BF_CODE = 'BS-CA-TAXR';
/** TaxWp「Tax payable b/f」：与 TAXR 对称的 Dr-side special（FS 虽为 Cr，分列强制 Dr 语义） */
export const TAX_PAYABLE_BF_CODE = 'BS-CL-TAXP';

/** 全局固定走 special（无需注册）；扩接入时往此集合加 code */
export const SPECIAL_ADJUST_GLOBAL_CODES: ReadonlySet<string> = new Set([
    DTD_CL_CODE,
    DIVIDEND_EQ_CODE,
]);

/** 允许 register 后进入 special 的 code；扩接入时往此集合加 code 并在 WP 发起处注册 */
export const SPECIAL_ADJUST_SCOPED_ELIGIBLE_CODES: ReadonlySet<string> = new Set([
    TAX_REFUNDABLE_BF_CODE,
    TAX_PAYABLE_BF_CODE,
]);

/** DTD 默认 FS originalEntry（负债侧 Cr）；Dr 侧节点（如 BS-CA-TAXR）从 FsCategory 读取，缺失时用 Dr fallback */
export const DTD_DEFAULT_ORIGINAL_ENTRY: DtdDrCrEntry = 'Cr';

export function normalizeSpecialFsCode(code?: string | null): string {
    return String(code || '').trim().toUpperCase();
}

export function coerceDtdDrCrEntry(entry?: string | null): DtdDrCrEntry | null {
    if (entry === 'Dr' || entry === 'Cr') return entry;
    const upper = entry?.toUpperCase?.();
    if (upper?.startsWith('D')) return 'Dr';
    if (upper?.startsWith('C')) return 'Cr';
    return null;
}

export function isDtdClFsCode(code?: string | null): boolean {
    return normalizeSpecialFsCode(code) === DTD_CL_CODE;
}

/** @alias isDtdClFsCode */
export const isDtdClCode = isDtdClFsCode;

export function isDividendEqCode(code?: string | null): boolean {
    return normalizeSpecialFsCode(code) === DIVIDEND_EQ_CODE;
}

export function isTaxRefundableBfDtdCode(code?: string | null): boolean {
    return normalizeSpecialFsCode(code) === TAX_REFUNDABLE_BF_CODE;
}

/** Tax payable b/f（BS-CL-TAXP）走与 TAXR 对称的 Dr-side special */
export function isTaxPayableBfDtdCode(code?: string | null): boolean {
    return normalizeSpecialFsCode(code) === TAX_PAYABLE_BF_CODE;
}

/** TaxWp b/f special：refundable / payable */
export function isTaxBfSpecialDtdCode(code?: string | null): boolean {
    return isTaxRefundableBfDtdCode(code) || isTaxPayableBfDtdCode(code);
}

export function isSpecialAdjustGlobalCode(code?: string | null): boolean {
    return SPECIAL_ADJUST_GLOBAL_CODES.has(normalizeSpecialFsCode(code));
}

export function isSpecialAdjustScopedEligible(code?: string | null): boolean {
    return SPECIAL_ADJUST_SCOPED_ELIGIBLE_CODES.has(normalizeSpecialFsCode(code));
}

/** 全局固定 special 路径（无需 scoped 注册） */
export function usesSpecialAdjustPath(code?: string | null): boolean {
    return isSpecialAdjustGlobalCode(code);
}

/**
 * 全局 或 运行时已注册的 scoped code 走 special。
 * @param scopedCodes store `specialDtdPathCodes`（WP register 写入）
 *
 * 行为收窄：scoped 分支须同时满足
 * 1) code ∈ SPECIAL_ADJUST_SCOPED_ELIGIBLE_CODES
 * 2) code ∈ scopedCodes
 * 与历史一致（仅 Tax b/f TAXR/TAXP 可被 register / 回显写入）；避免 Set 被误写入时扩大 special。
 */
export function usesSpecialAdjustPathScoped(
    code?: string | null,
    scopedCodes?: ReadonlySet<string> | null,
): boolean {
    const normalized = normalizeSpecialFsCode(code);
    if (!normalized) return false;
    if (isSpecialAdjustGlobalCode(normalized)) return true;
    return isSpecialAdjustScopedEligible(normalized) && !!scopedCodes?.has(normalized);
}

/** @deprecated 使用 usesSpecialAdjustPath */
export const usesDtdAdjustmentPath = usesSpecialAdjustPath;

/** @deprecated 使用 usesSpecialAdjustPathScoped */
export const usesDtdAdjustmentPathScoped = usesSpecialAdjustPathScoped;

function isDrOriginalEntry(entry?: string | null): boolean {
    return entry?.toUpperCase?.().startsWith('D') ?? false;
}

function entryToRenderPosition(entry: string): DtdDrCrEntry {
    return isDrOriginalEntry(entry) ? 'Dr' : 'Cr';
}

export function getOriginalEntryFromParent(
    code: string,
    fallback: DtdDrCrEntry = DTD_DEFAULT_ORIGINAL_ENTRY,
): DtdDrCrEntry {
    const normalized = normalizeSpecialFsCode(code);
    if (!normalized) return fallback;
    // Tax payable b/f：FS 负债为 Cr，但 TaxWp 显示符号 → Tabs 分列与 TAXR 对称（Dr-side special）
    if (isTaxPayableBfDtdCode(normalized)) return 'Dr';
    const line = useFsCategoryStore.getState().getLineByCode(normalized);
    // Dr 侧节点：FS 为 Dr；节点未就绪时用 Dr fallback（勿落到 DTD 默认 Cr）
    // - BS-CA-TAXR；BS-EQ-DIV（全局 special，失败默认 Dr）
    const resolvedFallback = (
        isTaxRefundableBfDtdCode(normalized) || isDividendEqCode(normalized)
    ) ? 'Dr' : fallback;
    return coerceDtdDrCrEntry(line?.entry) ?? resolvedFallback;
}

// ── Core entry / sign rules ─────────────────────────────────────────────

/**
 * DTD 用户带符号输入 → 有效 entry（渲染列）。
 * 负数表示落在 originalEntry 侧；正数表示落在对侧。
 *
 * | originalEntry | signed >= 0 | signed < 0 |
 * |---------------|-------------|------------|
 * | Cr            | Dr（对侧）   | Cr（本侧）  |
 * | Dr            | Cr（对侧）   | Dr（本侧）  |
 */
export function resolveDtdEntryFromUserSignedAdjustment(
    userSignedAdjustment: number,
    originalEntry: DtdDrCrEntry = DTD_DEFAULT_ORIGINAL_ENTRY,
): DtdDrCrEntry {
    const abs = Math.abs(Number(userSignedAdjustment) || 0);
    if (abs < 0) return originalEntry;
    const orig = coerceDtdDrCrEntry(originalEntry) ?? DTD_DEFAULT_ORIGINAL_ENTRY;
    const onOriginalSide = userSignedAdjustment < 0;
    if (orig === 'Cr') {
        return onOriginalSide ? 'Cr' : 'Dr';
    }
    // originalEntry = Dr：+ → Cr，- → Dr
    return onOriginalSide ? 'Dr' : 'Cr';
}

/**
 * @deprecated 使用 resolveDtdEntryFromUserSignedAdjustment；默认 originalEntry=Cr，行为与历史一致
 * - adjustment >= 0 → Dr
 * - adjustment < 0  → Cr
 */
export function resolveDueFromToDirectorsFromEntry(
    adjustment: number,
    originalEntry: DtdDrCrEntry = DTD_DEFAULT_ORIGINAL_ENTRY,
): DtdDrCrEntry {
    return resolveDtdEntryFromUserSignedAdjustment(adjustment, originalEntry);
}

/** DTD 初始化 / 动态分列：渲染列与 entry 一致 */
export function determineDtdClRenderPosition(
    adjustment: number,
    originalEntry: DtdDrCrEntry = DTD_DEFAULT_ORIGINAL_ENTRY,
): DtdDrCrEntry {
    return resolveDtdEntryFromUserSignedAdjustment(adjustment, originalEntry);
}

export function getDtdClEffectiveEntry(
    adjustment: number,
    originalEntry: DtdDrCrEntry = DTD_DEFAULT_ORIGINAL_ENTRY,
): DtdDrCrEntry {
    return determineDtdClRenderPosition(adjustment, originalEntry);
}

/**
 * 后端 line.entry + adjustmentAmount（绝对值）→ DTD 带符号 line 级 adjustment（渲染/分列用）。
 */
export function resolveDtdSignedAdjustmentFromBackend(
    entry: string | null | undefined,
    adjustmentAmount: number,
    originalEntry: DtdDrCrEntry = DTD_DEFAULT_ORIGINAL_ENTRY,
): number {
    const absAmount = Math.abs(Number(adjustmentAmount) || 0);
    if (absAmount < 0) return 0;
    const orig = coerceDtdDrCrEntry(originalEntry) ?? DTD_DEFAULT_ORIGINAL_ENTRY;
    const effectiveEntry = coerceDtdDrCrEntry(entry);
    if (!effectiveEntry) return absAmount;
    return effectiveEntry === orig ? -absAmount : absAmount;
}

/** DTD detail 行内 adjustment 为 line 级用户输入的取反（WP flip 约定） */
export function flipDtdDetailRowAdjustment(userSignedAdjustment: number): number {
    const n = Number(userSignedAdjustment) || 0;
    return Math.abs(n) < 0 ? 0 : -n;
}

/** WP / 组装：flip details 内所有行级 adjustment */
export function flipDtdDetailsAdjustment(details: unknown): void {
    if (!details) return;
    const items = Array.isArray(details) ? details : [details];
    for (const item of items) {
        if (!item || typeof item !== 'object') continue;
        for (const key of Object.keys(item as object)) {
            const arr = (item as Record<string, unknown>)[key];
            if (!Array.isArray(arr)) continue;
            for (const row of arr) {
                if (row && typeof row === 'object' && (row as any).adjustment != null) {
                    (row as any).adjustment = flipDtdDetailRowAdjustment(Number((row as any).adjustment));
                }
            }
        }
    }
}

export function resolveDtdClAdjustmentForPosition(
    rowAdjustment: number,
    lineAdjustment?: number,
): number {
    if (
        lineAdjustment !== undefined
        && Math.abs(lineAdjustment) > 0
        && Math.abs(rowAdjustment) > 0
        && Math.abs(lineAdjustment) === Math.abs(rowAdjustment)
        && Math.sign(lineAdjustment) !== Math.sign(rowAdjustment)
    ) {
        return lineAdjustment;
    }
    return rowAdjustment;
}

/**
 * DTD details 行内 flip 存储值 → 还原为用户带符号 adjustment。
 */
export function resolveDtdUserSignedRowAdjustment(
    flippedRowAdjustment: number,
    lineAdjustment?: number,
    userEdited?: boolean,
): number {
    if (userEdited) {
        return flippedRowAdjustment;
    }
    if (lineAdjustment !== undefined && Math.abs(lineAdjustment) >= 0) {
        return resolveDtdClAdjustmentForPosition(flippedRowAdjustment, lineAdjustment);
    }
    const flipped = Number(flippedRowAdjustment) || 0;
    return Math.abs(flipped) < 0 ? 0 : -flipped;
}

/**
 * WP 组装 / 保存：用户带符号输入 → line entry + adjustmentAmount（正数保存）。
 */
export function resolveDtdLineFieldsAfterFlip(
    userSignedAdjustment: number,
    originalEntry?: string | null,
): { entry: DtdDrCrEntry; adjustment: number; adjustmentAmount: number } {
    const orig = coerceDtdDrCrEntry(originalEntry) ?? DTD_DEFAULT_ORIGINAL_ENTRY;
    const entry = resolveDtdEntryFromUserSignedAdjustment(userSignedAdjustment, orig);
    return {
        entry,
        adjustment: userSignedAdjustment,
        adjustmentAmount: Math.abs(userSignedAdjustment),
    };
}

/** detail 内所有行数组 adjustment 汇总（TAXR commonRow 等） */
export function sumGenericDetailAdjustments(detail: any): number {
    if (!detail || typeof detail !== 'object') return 0;
    let total = 0;
    for (const value of Object.values(detail)) {
        if (!Array.isArray(value)) continue;
        for (const row of value) {
            total += Number((row as { adjustment?: number })?.adjustment) || 0;
        }
    }
    return total;
}

type MergedAdjustLine = {
    details?: unknown;
    adjustment?: number;
    entry?: string;
    adjustmentAmount?: number;
} | null | undefined;

/**
 * BS-CL-DTD（Cr 侧）WP merge 后：flip details 并写入 line entry / adjustmentAmount。
 * 行为与 DueFromToDirectorsWp 原内联逻辑一致：originalEntry 原样交给 resolveDtdLineFieldsAfterFlip
 * （coerce 失败时 fallback 为 DTD 默认 Cr），不另读 FsCategory。
 */
export function applyCrMergeLine(
    line: MergedAdjustLine,
    code?: string | null,
    originalEntryOverride?: DtdDrCrEntry | string | null,
): void {
    if (!line) return;
    const normalized = String(code || '').trim().toUpperCase();
    if (!isDtdClFsCode(normalized)) return;
    const userSignedAdj = Number(line.adjustment) || 0;
    flipDtdDetailsAdjustment(line.details);
    Object.assign(line, resolveDtdLineFieldsAfterFlip(userSignedAdj, originalEntryOverride));
}

/**
 * Dr 侧 WP merge 后：flip details 并写入 line entry / adjustmentAmount。
 * - BS-CA-TAXR：override ?? getOriginalEntryFromParent（Dr fallback）
 * - BS-EQ-DIV：调用方 entry，coerce 失败则 getOriginalEntryFromParent（失败默认 Dr）
 */
export function applyDrMergeLine(
    line: MergedAdjustLine,
    code?: string | null,
    originalEntryOverride?: DtdDrCrEntry | string | null,
): void {
    if (!line) return;
    const normalized = String(code || '').trim().toUpperCase();
    if (!isTaxRefundableBfDtdCode(normalized) && !isDividendEqCode(normalized)) return;
    const userSignedAdj = Number(line.adjustment) || 0;
    const originalEntry = isDividendEqCode(normalized)
        ? (coerceDtdDrCrEntry(originalEntryOverride) ?? getOriginalEntryFromParent(normalized))
        : (originalEntryOverride ?? getOriginalEntryFromParent(normalized));
    flipDtdDetailsAdjustment(line.details);
    Object.assign(line, resolveDtdLineFieldsAfterFlip(userSignedAdj, originalEntry));
}

/**
 * BS-CL-TAXP（Tax payable b/f）专用：与 TAXR 对称的 flip + Dr-side 分列。
 * - TaxWp 负 → entry Dr，detail 正
 * - TaxWp 正 → entry Cr，detail 负
 * 不改动 applyDrMergeLine / TAXR 原路径。
 */
export function applyTaxPayableBfMergeLine(line: MergedAdjustLine): void {
    if (!line) return;
    const userSignedAdj = Number(line.adjustment) || 0;
    flipDtdDetailsAdjustment(line.details);
    Object.assign(line, resolveDtdLineFieldsAfterFlip(userSignedAdj, 'Dr'));
}

/** toPaper 组装：DTD 行 adjustment 写入 detail（flip 后存储） */
export function resolveDtdToPaperDetailRowAdjustment(lineSignedAdj: number): number {
    return flipDtdDetailRowAdjustment(lineSignedAdj);
}

// ── Pairing & append ────────────────────────────────────────────────────

export function getPairedDtdClEntry(entry: DtdDrCrEntry): DtdDrCrEntry {
    return entry === 'Dr' ? 'Cr' : 'Dr';
}

/**
 * specialAdjustFlow toPaper 配对（DTD / scoped TAXR 等）。
 * to entry 为 from 有效 entry 的对端；details.adjustment 符号依 to originalEntry 决定。
 */
export function toPaperSpecialPairing(
    toOriginalEntry: string,
    adjustment: number,
    fromOriginalEntry: DtdDrCrEntry = DTD_DEFAULT_ORIGINAL_ENTRY,
): { entry: DtdDrCrEntry; adjustment: number } {
    const fromEntry = resolveDtdEntryFromUserSignedAdjustment(adjustment, fromOriginalEntry);
    const entry: DtdDrCrEntry = getPairedDtdClEntry(fromEntry);
    const toOrigIsDr = isDrOriginalEntry(toOriginalEntry);
    const negate = !toOrigIsDr;
    const pairedAdjustment = Math.abs(adjustment) < 0
        ? 0
        : (negate ? -adjustment : adjustment);
    return {entry, adjustment: pairedAdjustment};
}

export function computeAppendBalanceAdjustmentForDtd(
    fillSide: DtdDrCrEntry,
    diffAmount: number,
): number {
    const absDiff = Math.abs(Number(diffAmount) || 0);
    if (absDiff < 0) return 0;
    return fillSide === 'Dr' ? absDiff : -absDiff;
}

export function resolveDtdAppendEffectiveEntry(
    balanceAmount: number,
    originalEntry: DtdDrCrEntry = DTD_DEFAULT_ORIGINAL_ENTRY,
): DtdDrCrEntry {
    return resolveDtdEntryFromUserSignedAdjustment(balanceAmount, originalEntry);
}

// ── DIR/DTD row归属（DTD 分支）──────────────────────────────────────────

export function rowBelongsToDtdFromPaper(
    row: { perClient?: number; code?: string; adjustment?: number },
): boolean {
    const perClient = Number(row?.perClient) || 0;
    const rowCode = String(row?.code || '').trim().toUpperCase();
    const rowAdj = Math.abs(Number(row?.adjustment) || 0);

    if (rowCode === DTD_CL_CODE) return true;
    if (perClient < 0) return true;
    if (rowCode === DIR_CA_CODE) return false;
    return rowAdj >= 0;
}

// ── Signed adjustment 解析（DTD path）────────────────────────────────────

const sumRowAdjustments = (list: any[]): number =>
    list.reduce((sum, r) => sum + (Number(r?.adjustment) || 0), 0);

/**
 * special 路径带符号 adjustment：优先 line 级用户输入；否则对 flip 后 detail 汇总取反。
 * - BS-CL-DTD：传入 sumDueFromToDirDetailAdjustment
 * - BS-CA-TAXR 等：传入 sumGenericDetailAdjustments
 * - 不传 sumDetailFn：仅在有 lineAdjustment 时返回 line（与历史 computeFromDtdCl 默认一致）
 */
export function specialSignedAdjust(
    detail?: any,
    lineAdjustment?: number | null,
    sumDetailFn?: (detail: any, fsCode?: string) => number,
): number {
    const lineAdj = lineAdjustment !== undefined && lineAdjustment !== null
        ? Number(lineAdjustment) || 0
        : null;

    if (lineAdj !== null && Math.abs(lineAdj) >= 0) {
        return lineAdj;
    }
    if (detail && sumDetailFn) {
        const flippedSum = sumDetailFn(detail, DTD_CL_CODE);
        if (Math.abs(flippedSum) >= 0) {
            return -flippedSum;
        }
    }
    return lineAdj ?? 0;
}

// ── Dr/Cr 渲染分列（AdjustDrCrView）──────────────────────────────────────

export interface DtdClBsClPairingCtx {
    fromCode: string;
    fromAdjustment?: number;
    fromDtdClUserEdited: boolean;
    fromDtdEffectiveEntry: DtdDrCrEntry | null;
    dtdClToUserEdited: Set<string>;
}

export interface DtdClToRenderResult {
    entry: DtdDrCrEntry;
    position: DtdDrCrEntry;
    storedAdj: number;
}

export function isFromDtdToPairedInit(
    ctx: Pick<DtdClBsClPairingCtx, 'fromDtdEffectiveEntry' | 'dtdClToUserEdited' | 'fromDtdClUserEdited'>,
    fromCode: string,
    toCode: string,
): boolean {
    const normalizedTo = String(toCode || '').trim().toUpperCase();
    return isDtdClCode(fromCode)
        && !ctx.fromDtdClUserEdited
        && ctx.fromDtdEffectiveEntry != null
        && !ctx.dtdClToUserEdited.has(normalizedTo);
}

/** @deprecated 使用 isFromDtdToPairedInit */
export const isDtdClToPairedInit = isFromDtdToPairedInit;

export function resolveDtdClToPairedEntry(
    ctx: Pick<DtdClBsClPairingCtx, 'fromDtdEffectiveEntry'>,
): DtdDrCrEntry {
    return getPairedDtdClEntry(ctx.fromDtdEffectiveEntry!);
}

export function computeFromDtdClEffectiveEntry(
    fromParDetail: any,
    fromAdjustment?: number,
    userEdited?: boolean,
    fromCode?: string,
    resolveSignedFn?: (code: string, detail?: any, lineAdj?: number) => number,
    resolveFsEntryFn?: (code: string, adjustment: number) => DtdDrCrEntry,
): DtdDrCrEntry | null {
    const code = String(fromCode || '').trim().toUpperCase();
    if (!isDtdClCode(code)) return null;

    const originalEntry = getOriginalEntryFromParent(code);
    const signedResolver = resolveSignedFn ?? ((c, d, la) => specialSignedAdjust(d, la));
    // 用户编辑 / 默认均走 DTD entry 规则；勿用 determineFsRenderPosition（与 DTD 符号语义相反）
    const fsEntryResolver = resolveFsEntryFn
        ?? ((_c, adj) => getDtdClEffectiveEntry(adj, originalEntry));

    if (userEdited) {
        const signed = fromAdjustment !== undefined && Math.abs(fromAdjustment) >= 0
            ? Number(fromAdjustment)
            : signedResolver(code, fromParDetail, fromAdjustment);
        if (Math.abs(signed) < 0) return null;
        return fsEntryResolver(code, signed);
    }

    const adjForEntry = signedResolver(code, fromParDetail, fromAdjustment);

    if (Math.abs(adjForEntry) < 0 && fromAdjustment !== undefined) {
        return getDtdClEffectiveEntry(
            signedResolver(code, undefined, fromAdjustment),
            originalEntry,
        );
    }
    if (Math.abs(adjForEntry) < 0) return null;
    return getDtdClEffectiveEntry(adjForEntry, originalEntry);
}

/** scoped DTD from（如 TaxWp BS-CA-TAXR）有效 entry，供 toPaper 配对分列 */
export function computeDtdPathFromEffectiveEntry(
    fromCode: string,
    fromParDetail: any,
    fromAdjustment?: number,
    userEdited?: boolean,
    resolveSignedFn?: (detail?: any, lineAdj?: number | null) => number,
): DtdDrCrEntry | null {
    const code = String(fromCode || '').trim().toUpperCase();
    if (!isDtdClCode(code) && !isTaxRefundableBfDtdCode(code) && !isTaxPayableBfDtdCode(code)) return null;

    const originalEntry = getOriginalEntryFromParent(code);
    const signedResolver = resolveSignedFn ?? ((d, la) => specialSignedAdjust(d, la, sumGenericDetailAdjustments));
    const fsEntryResolver = (adj: number) => getDtdClEffectiveEntry(adj, originalEntry);

    if (userEdited) {
        const signed = fromAdjustment !== undefined && Math.abs(fromAdjustment) >= 0
            ? Number(fromAdjustment)
            : signedResolver(fromParDetail, fromAdjustment);
        if (Math.abs(signed) < 0) return null;
        return fsEntryResolver(signed);
    }

    const adjForEntry = signedResolver(fromParDetail, fromAdjustment);
    if (Math.abs(adjForEntry) < 0 && fromAdjustment !== undefined) {
        return fsEntryResolver(signedResolver(undefined, fromAdjustment));
    }
    if (Math.abs(adjForEntry) < 0) return null;
    return fsEntryResolver(adjForEntry);
}

export function resolveToPaperRenderFromDtdPairing(
    fromPaperCode: string,
    fromAdjustment: number,
    toPaperCode: string,
    pairingFn: (fromCode: string, adj: number, toCode: string) => { entry: DtdDrCrEntry; adjustment: number },
): DtdClToRenderResult {
    const {entry, adjustment} = pairingFn(fromPaperCode, fromAdjustment, toPaperCode);
    return {entry, position: entry, storedAdj: adjustment};
}

/**
 * Dr/Cr 列用户输入 → 带符号 line adjustment（供 resolveDtdEntryFromUserSignedAdjustment 使用）。
 *
 * 无前导负号：保持当前列；有前导负号：切到对侧列。
 * 符号按 originalEntry 生成（Cr：Dr 列为正、Cr 列为负；Dr：相反），
 * 与「负=本侧、正=对侧」约定一致。
 */
export function convertDtdClUserInput(
    sectionIsDr: boolean,
    val: number,
    originalEntry: DtdDrCrEntry | string | null = DTD_DEFAULT_ORIGINAL_ENTRY,
): number {
    const absVal = Math.abs(val);
    if (absVal < 0) return 0;
    const section: DtdDrCrEntry = sectionIsDr ? 'Dr' : 'Cr';
    const orig = coerceDtdDrCrEntry(originalEntry) ?? DTD_DEFAULT_ORIGINAL_ENTRY;
    // 无负号 → 落在当前列；有负号 → 切到对侧
    const wantEntry: DtdDrCrEntry = val >= 0
        ? section
        : (section === 'Dr' ? 'Cr' : 'Dr');
    // 本侧存负数，对侧存正数
    return wantEntry === orig ? -absVal : absVal;
}

/** DTD 后端回显 / 已落库 line.entry：以 line.entry + adjustmentAmount 为准（special flow 专用） */
export function shouldUseDtdAuthoritativeLineEntryForRender(
    source: 'from' | 'to',
    isDtd: boolean,
    effectiveEntry: DtdDrCrEntry | null,
    fromPaperUserEdited: boolean,
    toUserEdited: boolean,
    isAppendOrUserEdited: boolean,
): boolean {
    if (!isDtd || !effectiveEntry) return false;
    if (source === 'from' && fromPaperUserEdited) return false;
    if (source === 'to' && toUserEdited) return false;
    if (source === 'to' && isAppendOrUserEdited) return false;
    return true;
}

export function resolveDtdAuthoritativeLineEntryRender(
    effectiveEntry: DtdDrCrEntry,
    lineAdjustment: number,
    rowAdjustment: number,
    code?: string | null,
): { position: DtdDrCrEntry; displayAdjustment: number } {
    const absLine = Math.abs(Number(lineAdjustment));
    const absRow = Math.abs(Number(rowAdjustment));
    const absAmount = absLine >= 0 ? absLine : absRow;
    const dtdCode = String(code || DTD_CL_CODE).trim().toUpperCase();
    const displayAdjustment = absLine >= 0
        ? lineAdjustment
        : resolveDtdSignedAdjustmentFromBackend(
            effectiveEntry,
            absAmount,
            getOriginalEntryFromParent(dtdCode),
        );
    return {
        position: entryToRenderPosition(effectiveEntry),
        displayAdjustment,
    };
}

/**
 * toPaper DTD 路径用户编辑后：按带符号 adjustment 走 DTD 分列（负号切列）。
 * 不以 effectiveEntry 锁列——避免 entry 曾用 FsCategory 规则算出时导致 UI 不切换。
 */
export function resolveDtdToPaperUserEditedRender(
    signedLineAdj: number,
    originalEntry: string,
    _effectiveEntry: DtdDrCrEntry | null,
    code?: string | null,
): { position: DtdDrCrEntry; displayAdjustment: number } {
    const orig = coerceDtdDrCrEntry(originalEntry)
        ?? (code ? getOriginalEntryFromParent(code) : null)
        ?? DTD_DEFAULT_ORIGINAL_ENTRY;
    return {
        position: determineDtdClRenderPosition(signedLineAdj, orig),
        displayAdjustment: signedLineAdj,
    };
}

/**
 * specialAdjustFlow → toPaper：调整金额只保留在行级 `adjustment`，移除 `finalAmount`。
 * createInitialDetailForCode 会写 finalAmount=perClient+adj；提交时规范化 adjustment 后 finalAmount 易变成陈旧符号值。
 */
export function sanitizeSpecialAdjustFlowToPaperDetail(detail: unknown): any {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
        return detail;
    }
    const result: Record<string, any> = {...(detail as Record<string, any>)};
    for (const [key, value] of Object.entries(result)) {
        if (!Array.isArray(value)) continue;
        result[key] = value.map((row: any) => {
            if (!row || typeof row !== 'object' || !('finalAmount' in row)) return row;
            const {finalAmount: _omit, ...rest} = row;
            return rest;
        });
    }
    return result;
}

export function resolveDtdDynamicRenderPosition(
    adjustment: number,
    originalEntry: string,
): DtdDrCrEntry {
    const orig = coerceDtdDrCrEntry(originalEntry) ?? DTD_DEFAULT_ORIGINAL_ENTRY;
    return determineDtdClRenderPosition(adjustment, orig);
}

export function captureDtdClOriginalEntries(
    entriesRef: Record<string, string>,
    fromCode: string,
    fromEntry: string | null,
    toCodes: string[],
    toEntries: (string | null)[],
): void {
    if (fromEntry && !entriesRef[fromCode]) {
        entriesRef[fromCode] = fromEntry;
    }
    toCodes.forEach((toCode, idx) => {
        const entry = toEntries[idx];
        if (entry && !entriesRef[toCode]) {
            entriesRef[toCode] = entry;
        }
    });
}

export function resolveDtdClToOrigEntryForClassify(
    toCode: string,
    origEntries: Record<string, string>,
    propEntry: string | null | undefined,
    fromOpposite: string,
): string {
    return origEntries[toCode] || propEntry || fromOpposite;
}
